import {
  BuilderComponentPlugin,
  IChunkBuilder,
  ICodeChunk,
  ICodeStruct,
  FileType,
  ChunkType,
} from '../types';

import { COMMON_CHUNK_NAME, COMMON_SUB_MODULE_NAME } from '../const/generator';
import { FILE_TYPE_FAMILY } from '../const/file';
import { isEmpty } from 'lodash';

interface ParsedImport {
  defaultImport: string | null;
  namespaceImport: string | null;
  namedImports: Set<string>;
}

// 匹配 import ... from 'pkg'; 语句
const IMPORT_RE = /import\s+([\s\S]*?)\s+from\s+['"](.*?)['"]\s*;/g;

/**
 * 从 import 语句字符串中解析出 package → 导入信息 的映射
 */
function parseImportStatements(content: string): Map<string, ParsedImport> {
  const result = new Map<string, ParsedImport>();
  IMPORT_RE.lastIndex = 0;
  let match: RegExpExecArray | null = IMPORT_RE.exec(content);

  while (match !== null) {
    const specifiers = match[1].trim();
    const pkg = match[2];

    const existing: ParsedImport = result.get(pkg) || {
      defaultImport: null,
      namespaceImport: null,
      namedImports: new Set<string>(),
    };

    // namespace import: import * as X from 'pkg'
    const nsMatch = specifiers.match(/\*\s+as\s+(\w+)/);
    if (nsMatch) {
      existing.namespaceImport = nsMatch[1];
    } else {
      // named imports: { A, B, C }
      const namedMatch = specifiers.match(/\{([^}]*)\}/);
      if (namedMatch) {
        namedMatch[1]
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((name) => existing.namedImports.add(name));
        // default import 在花括号之前: import Foo, { A } from 'pkg'
        const before = specifiers.substring(0, specifiers.indexOf('{'));
        const defaultName = before.replace(/,\s*$/, '').trim();
        if (defaultName) {
          existing.defaultImport = defaultName;
        }
      } else {
        // 纯 default import: import Foo from 'pkg'
        existing.defaultImport = specifiers;
      }
    }

    result.set(pkg, existing);
    match = IMPORT_RE.exec(content);
  }

  return result;
}

/**
 * 将 ParsedImport 渲染成 import 语句字符串
 */
function renderImportEntry(pkg: string, spec: ParsedImport): string {
  const parts: string[] = [];
  if (spec.defaultImport && spec.namedImports.size > 0) {
    // 合并为单条: import Default, { A, B } from 'pkg';
    const names = Array.from(spec.namedImports).join(', ');
    parts.push(`import ${spec.defaultImport}, { ${names} } from '${pkg}';`);
  } else if (spec.defaultImport) {
    parts.push(`import ${spec.defaultImport} from '${pkg}';`);
  } else if (spec.namedImports.size > 0) {
    const names = Array.from(spec.namedImports).join(', ');
    parts.push(`import { ${names} } from '${pkg}';`);
  }
  if (spec.namespaceImport) {
    parts.push(`import * as ${spec.namespaceImport} from '${pkg}';`);
  }
  return parts.join('\n');
}

/**
 * 合并同一 subModule 下所有 ExternalDepsImport chunks，
 * 按 package 维度去重合并，替换为一个整合后的 chunk。
 *
 * 解决 esmodule 插件和 functionJsx/containerStatement 插件
 * 各自生成 ExternalDepsImport chunk 导致同 package 出现多条 import 的问题。
 */
function consolidateExternalImports(chunks: ICodeChunk[], subModule: string): void {
  // 收集目标 chunks 及其在数组中的索引
  const targetIndices: number[] = [];
  let templateChunk: ICodeChunk | null = null;

  for (let i = 0; i < chunks.length; i++) {
    const c = chunks[i];
    const sm = c.subModule || COMMON_SUB_MODULE_NAME;
    if (c.name === COMMON_CHUNK_NAME.ExternalDepsImport && sm === subModule) {
      targetIndices.push(i);
      if (!templateChunk) templateChunk = c;
    }
  }

  // 只有一个或没有 chunk，无需合并
  if (targetIndices.length <= 1 || !templateChunk) return;

  // 解析所有 chunk 的 content，按 package 合并
  const merged = new Map<string, ParsedImport>();
  for (const idx of targetIndices) {
    const parsed = parseImportStatements(String(chunks[idx].content));
    for (const [pkg, spec] of parsed) {
      const existing: ParsedImport = merged.get(pkg) || {
        defaultImport: null,
        namespaceImport: null,
        namedImports: new Set<string>(),
      };
      if (spec.defaultImport) existing.defaultImport = spec.defaultImport;
      if (spec.namespaceImport) existing.namespaceImport = spec.namespaceImport;
      spec.namedImports.forEach((n) => existing.namedImports.add(n));
      merged.set(pkg, existing);
    }
  }

  // 倒序删除旧 chunks
  for (let i = targetIndices.length - 1; i >= 0; i--) {
    chunks.splice(targetIndices[i], 1);
  }

  // 生成合并后的单个 chunk
  const content = Array.from(merged.entries())
    .map(([pkg, spec]) => renderImportEntry(pkg, spec))
    .filter(Boolean)
    .join('\n');

  if (content) {
    chunks.push({
      type: ChunkType.STRING,
      fileType: templateChunk.fileType,
      subModule,
      name: COMMON_CHUNK_NAME.ExternalDepsImport,
      content: `${content}\n`,
      linkAfter: [],
    });
  }
}

interface ChunkGroupInfo {
  chunk: ICodeChunk;
  familyIdx?: number;
}

function whichFamily(type: FileType): [number, FileType[]] | undefined {
  const idx = FILE_TYPE_FAMILY.findIndex((family) => family.indexOf(type) >= 0);
  if (idx < 0) {
    return undefined;
  }
  return [idx, FILE_TYPE_FAMILY[idx]];
}

export const groupChunks = (chunks: ICodeChunk[]): ICodeChunk[][] => {
  const tmp: Record<string, Record<number, number>> = {};
  const col = chunks.reduce((chunksSet: Record<string, ChunkGroupInfo[]>, chunk) => {
    const fileKey = chunk.subModule || COMMON_SUB_MODULE_NAME;
    if (!chunksSet[fileKey]) {
      // eslint-disable-next-line no-param-reassign
      chunksSet[fileKey] = [];
    }
    const res = whichFamily(chunk.fileType as FileType);
    const info: ChunkGroupInfo = {
      chunk,
    };
    if (res) {
      const [familyIdx, family] = res;
      const rank = family.indexOf(chunk.fileType as FileType);
      if (tmp[fileKey]) {
        if (tmp[fileKey][familyIdx] !== undefined) {
          if (tmp[fileKey][familyIdx] > rank) {
            tmp[fileKey][familyIdx] = rank;
          }
        } else {
          tmp[fileKey][familyIdx] = rank;
        }
      } else {
        tmp[fileKey] = {};
        tmp[fileKey][familyIdx] = rank;
      }
      info.familyIdx = familyIdx;
    }

    chunksSet[fileKey].push(info);
    return chunksSet;
  }, {});

  const result: ICodeChunk[][] = [];
  Object.keys(col).forEach((key) => {
    const byType: Record<string, ICodeChunk[]> = {};
    col[key].forEach((info) => {
      let t: string = info.chunk.fileType;
      if (info.familyIdx !== undefined) {
        t = FILE_TYPE_FAMILY[info.familyIdx][tmp[key][info.familyIdx]];
        // eslint-disable-next-line no-param-reassign
        info.chunk.fileType = t;
      }
      if (!byType[t]) {
        byType[t] = [];
      }
      byType[t].push(info.chunk);
    });
    result.push(...Object.keys(byType).map((t) => byType[t]));
  });

  return result;
};

/**
 * 代码片段构建器
 *
 * @export
 * @class ChunkBuilder
 * @template T
 */
export class ChunkBuilder implements IChunkBuilder {
  private plugins: BuilderComponentPlugin[];

  constructor(plugins: BuilderComponentPlugin[] = []) {
    this.plugins = plugins;
  }

  async run(
    ir: unknown,
    initialStructure: ICodeStruct = {
      ir,
      chunks: [],
      depNames: [],
      contextData: {},
      imports: {},
    },
  ) {
    const structure = initialStructure;
    const finalStructure: ICodeStruct = await this.plugins.reduce(
      async (previousPluginOperation: Promise<ICodeStruct>, plugin) => {
        const modifiedStructure = await previousPluginOperation;
        return plugin(modifiedStructure);
      },
      Promise.resolve(structure),
    );
    // 1. 将 next.imports 生成为 ExternalDepsImport chunk
    if (!isEmpty(finalStructure.imports)) {
      const content = Object.entries(finalStructure.imports)
        .map(([pkg, spec]) => renderImportEntry(pkg, spec))
        .filter(Boolean)
        .join('\n');
      if (content) {
        finalStructure.chunks.push({
          type: ChunkType.STRING,
          fileType: FileType.TSX,
          subModule: COMMON_SUB_MODULE_NAME,
          name: COMMON_CHUNK_NAME.ExternalDepsImport,
          content: `${content}\n`,
          linkAfter: [],
        });
      }
    }
    // 2. 合并所有 subModule 下的 ExternalDepsImport chunks（按 package 去重）
    const subModules = new Set(
      finalStructure.chunks.map((c) => c.subModule || COMMON_SUB_MODULE_NAME),
    );
    for (const subModule of subModules) {
      consolidateExternalImports(finalStructure.chunks, subModule);
    }
    const chunks = groupChunks(finalStructure.chunks);

    return {
      chunks,
    };
  }

  getPlugins() {
    return this.plugins;
  }

  addPlugin(plugin: BuilderComponentPlugin) {
    this.plugins.push(plugin);
  }
}

export default ChunkBuilder;
